import { gsap } from '../gsap-setup';

export const menuAnimation = () => {
	const menuToggle = document.getElementById('menu-toggle');
	const fullscreenMenu = document.getElementById('fullscreen-menu');
	const menuBg = fullscreenMenu?.querySelector('.menu-bg');
	const menuItems = fullscreenMenu?.querySelectorAll('.menu-items li');
	const navBar = document.querySelector('.header');
	const menuClose = document.getElementById('menu-close');

	if (!menuToggle || !fullscreenMenu) return;

	let isMenuOpen = false;

	// Helper - keep toggle class in sync
	const setToggleState = (open) => {
		isMenuOpen = open;
		menuToggle.classList.toggle('active', open);
	};

	// Set initial state
	gsap.set(fullscreenMenu, { visibility: 'hidden' });
	gsap.set(menuBg, { scaleY: 0, transformOrigin: 'top' });
	gsap.set(menuItems, { opacity: 0, y: 30 });
	if (navBar) {
		gsap.set(navBar, { '--border-scale': 0 });
	}

	// Open menu animation
	const openMenu = () => {
		const tl = gsap.timeline();

		tl.set(fullscreenMenu, { visibility: 'visible' })
			.to(menuBg, {
				scaleY: 1,
				duration: 0.6,
				ease: 'power3.inOut'
			})
		tl.to(
			navBar,
			{
				'--border-scale': 1,
				backgroundColor: '#FFFFFF',
				duration: 0.3,
				ease: 'power2.out'
			},
			'-=0.2'
		)
			.to(
				menuItems,
				{
					opacity: 1,
					y: 0,
					duration: 0.5,
					stagger: 0.1,
					ease: 'power2.out'
				},
				'-=0.2'
			);


		document.body.classList.add('menu-open');
	};

	// Close menu animation
	const closeMenu = () => {
		const tl = gsap.timeline();

		tl.to(menuItems, {
			opacity: 0,
			y: -30,
			duration: 0.3,
			stagger: 0.05,
			ease: 'power2.in'
		})
			.to(
				menuBg,
				{
					scaleY: 0,
					duration: 0.5,
					ease: 'power3.inOut'
				},
				'-=0.3'
			)
			.set(fullscreenMenu, { visibility: 'hidden' });

		if (navBar) {
			tl.to(
				navBar,
				{
					'--border-scale': 0,
					backgroundColor: 'transparent',
					duration: 0.2,
					ease: 'power2.in'
				},
				'-=0.4'
			);
		}

		document.body.classList.remove('menu-open');
	};

	// Event listener - toggle menu open/close
	menuToggle.addEventListener('click', () => {
		if (isMenuOpen) {
			closeMenu();
			setToggleState(false);
		} else {
			openMenu();
			setToggleState(true);
		}
	});

	if (menuClose) {
		menuClose.addEventListener('click', () => {
			if (!isMenuOpen) return;
			closeMenu();
			setToggleState(false);
		});
	}

	if (menuBg) {
		menuBg.addEventListener('click', () => {
			if (!isMenuOpen) return;
			closeMenu();
			setToggleState(false);
		});
	}

	const menuLinks = fullscreenMenu.querySelectorAll('.menu-items a');
	menuLinks.forEach((link) => {
		link.addEventListener('click', (e) => {
			const parentLi = link.closest('li');
			const hasSubmenu =
				parentLi && parentLi.classList.contains('menu-item-has-children');

			// If it's a top-level link with a submenu, do not close the menu
			if (hasSubmenu && link === parentLi.querySelector(':scope > a')) {
				e.preventDefault();
				return;
			}

			if (!isMenuOpen) return;
			closeMenu();
			setToggleState(false);
		});
	});

	// Close on escape key
	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape' && document.body.classList.contains('menu-open')) {
			closeMenu();
			setToggleState(false);
		}
	});
};

menuAnimation();

(() => {
	const follower = document.querySelector('.menu-cursor-follower');
	if (!follower) return;

	// Don’t run on touch devices
	if (window.matchMedia('(pointer: coarse)').matches) return;

	let enabled = false;

	let x = 0,
		y = 0;
	let tx = 0,
		ty = 0;

	const size = 18; // match CSS
	const ease = 0.18;

	const onMove = (e) => {
		if (!enabled) return;
		tx = e.clientX;
		ty = e.clientY;
	};

	const tick = () => {
		x += (tx - x) * ease;
		y += (ty - y) * ease;
		follower.style.transform = `translate3d(${x - size / 2}px, ${y - size / 2}px, 0)`;
		requestAnimationFrame(tick);
	};

	requestAnimationFrame(tick);
	window.addEventListener('mousemove', onMove, { passive: true });

	// Enable/disable based on body class changes (GSAP code already toggles menu-open)
	const updateEnabled = () => {
		enabled = document.body.classList.contains('menu-open');
	};

	// Initial state
	updateEnabled();

	// Observe body class changes
	const obs = new MutationObserver(updateEnabled);
	obs.observe(document.body, { attributes: true, attributeFilter: ['class'] });

	// Optional: hide when leaving window
	window.addEventListener('mouseleave', () => {
		follower.style.opacity = '0';
	});
	window.addEventListener('mouseenter', () => {
		if (document.body.classList.contains('menu-open')) follower.style.opacity = '';
	});
})();

// Navigation overlay toggle and scroll behavior

export const navigation = () => {
	const fullscreenMenu = document.getElementById('fullscreen-menu');
	const header = document.querySelector('.header');

	if (!fullscreenMenu || !header) return;

	// Submenu accordion toggle
	const toggleSubmenu = (menuItem) => {
		const isOpen = menuItem.classList.contains('submenu-open');

		// Close all other submenus at the same level
		const siblings = menuItem.parentElement.children;
		Array.from(siblings).forEach((sibling) => {
			if (sibling !== menuItem && sibling.classList.contains('menu-item-has-children')) {
				sibling.classList.remove('submenu-open');
			}
		});

		// Toggle this submenu
		if (isOpen) {
			menuItem.classList.remove('submenu-open');
		} else {
			menuItem.classList.add('submenu-open');
		}
	};

	// Submenu click handling (no menu open/close here)
	const menuLinks = fullscreenMenu.querySelectorAll('.menu-items a');
	menuLinks.forEach((link) => {
		link.addEventListener('click', (e) => {
			const parentLi = link.closest('li');
			const hasSubmenu = parentLi && parentLi.classList.contains('menu-item-has-children');

			if (hasSubmenu && link === parentLi.querySelector(':scope > a')) {
				e.preventDefault();
				toggleSubmenu(parentLi);
			}
		});
	});

	// Handle scroll behavior - add class when scrolled
	let lastScroll = 0;
	const handleScroll = () => {
		const currentScroll = window.pageYOffset;

		// Add scrolled class when past 50px
		if (currentScroll > 50) {
			header.classList.add('scrolled');
		} else {
			header.classList.remove('scrolled');
		}

		lastScroll = currentScroll;
	};

	// Throttle scroll events
	let scrollTimeout;
	window.addEventListener('scroll', () => {
		if (scrollTimeout) {
			window.cancelAnimationFrame(scrollTimeout);
		}
		scrollTimeout = window.requestAnimationFrame(() => {
			handleScroll();
		});
	});

};

// Initialize navigation
navigation();
